import React, { useState } from 'react'
import { Download } from 'lucide-react'
import { Document, Packer, Paragraph, TextRun, AlignmentType, HeadingLevel } from 'docx'

interface ClaimsWithNumeralsDisplayProps {
  result: any;
  theme?: 'light' | 'dark';
  searchTerm?: string;
  matchesWildcard?: (text: string, pattern: string) => boolean;
  showToggle?: boolean;
  onAddToInput?: (text: string) => void;
  onCopyResult?: () => void;
  showToast?: (message: string) => void;
  showHeader?: boolean;
}

const ClaimsWithNumeralsDisplay: React.FC<ClaimsWithNumeralsDisplayProps> = ({
  result,
  theme = 'light',
  searchTerm = '',
  matchesWildcard,
  showToggle = false,
  onAddToInput,
  onCopyResult,
  showToast,
  showHeader = true
}) => {
  const [showStructured, setShowStructured] = useState(false)

  // Check if the result looks like a claims with numerals object
  const isClaimsWithNumeralsResult = (data) => {
    if (!data || typeof data !== 'object') return false
    
    // Check for new format: numbered claims with content
    const keys = Object.keys(data)
    if (keys.length === 0) return false
    
    // Check if entries are numbered and have content (not features/characteristics)
    let validEntries = 0
    keys.forEach(key => {
      // Must be a number key
      if (!/^\d+$/.test(key)) return
      
      const entry = data[key]
      if (entry && typeof entry === 'object' && entry.content && typeof entry.content === 'string') {
        // Make sure it's not claims support format (which has features with characteristics)
        const hasFeatures = Object.keys(entry).some(k => k !== 'content' && 
          entry[k] && typeof entry[k] === 'object' && 
          (entry[k].characteristics || entry[k].children !== undefined))
        
        if (!hasFeatures) {
          validEntries++
        }
      }
    })
    
    // If we found numbered claims with content, consider it claims with numerals
    if (validEntries > 0) {
      return true
    }
    
    // Fallback: Check for legacy number_claims property with output
    if (data.number_claims && typeof data.number_claims === 'object' && data.number_claims.output) {
      return true
    }
    
    return false
  }

  if (!isClaimsWithNumeralsResult(result)) {
    return null // Not a claims with numerals result, don't render this component
  }

  // Handle both new and legacy data formats
  const isLegacyFormat = result.number_claims && typeof result.number_claims === 'object' && result.number_claims.output
  
  const parseClaimsText = (text) => {
    if (!text) return []
    
    // Split claims by pattern "Claim X." or numbered claims
    const claimPattern = /(?:^|\n)\s*(?:Claim\s+)?(\d+)\.\s*/gi
    const parts = text.split(claimPattern)
    const claims = []
    
    for (let i = 1; i < parts.length; i += 2) {
      const claimNumber = parts[i]
      const claimText = parts[i + 1]?.trim()
      if (claimText) {
        claims.push({
          number: parseInt(claimNumber),
          text: claimText
        })
      }
    }
    
    // If no structured claims found, treat as single block
    if (claims.length === 0) {
      return [{
        number: 1,
        text: text.trim()
      }]
    }
    
    return claims
  }

  const parseNewFormatClaims = (data) => {
    const claims = []
    Object.entries(data).forEach(([claimNum, claimData]: [string, any]) => {
      if (claimData && claimData.content) {
        claims.push({
          number: parseInt(claimNum),
          text: claimData.content
        })
      }
    })
    return claims.sort((a, b) => a.number - b.number)
  }

  const highlightNumerals = (text) => {
    // Highlight reference numerals in parentheses only
    // Avoid highlighting standalone numbers to prevent conflicts
    const spanClass = `font-bold ${theme === 'dark' ? 'text-blue-400' : 'text-blue-600'} bg-opacity-20 ${theme === 'dark' ? 'bg-blue-900' : 'bg-blue-100'} rounded`
    
    // Highlight various numeral patterns in parentheses:
    // - Regular numbers: (1), (2), etc.
    // - Alphanumerical: (R1), (A2), (L10), etc.
    // - Numbers with prime/apostrophe: (4'), (1′), etc.
    let processedText = text
      .replace(/\((\d+)\)/g, `<span class="${spanClass}">($1)</span>`) // Regular numbers
      .replace(/\(([A-Za-z]\d+)\)/g, `<span class="${spanClass}">($1)</span>`) // Alphanumerical like (R1)
      .replace(/\((\d+['′])\)/g, `<span class="${spanClass}">($1)</span>`) // Numbers with prime/apostrophe
    
    return processedText
  }

  const exportToDocx = async () => {
    try {
      const claims = isLegacyFormat
        ? parseClaimsText(result.number_claims.output)
        : parseNewFormatClaims(result)

      // Helper function to parse claim text and create TextRun objects with bold numerals
      const parseClaimTextWithNumerals = (text: string): TextRun[] => {
        const textRuns: TextRun[] = [];
        // Match reference numerals in parentheses: (1), (R1), (4'), etc.
        const numeralPattern = /(\([A-Za-z]?\d+['′]?\))/g;
        const parts = text.split(numeralPattern);

        parts.forEach((part, index) => {
          if (!part) return;

          // Check if this part is a numeral (odd indices after split)
          if (numeralPattern.test(part)) {
            textRuns.push(new TextRun({
              text: part,
              bold: true,
            }));
          } else {
            textRuns.push(new TextRun({
              text: part,
            }));
          }
        });

        return textRuns;
      };

      // Create Word document
      const doc = new Document({
        sections: [{
          properties: {},
          children: [
            // Title
            new Paragraph({
              text: "Claims with Reference Numerals",
              heading: HeadingLevel.HEADING_1,
              alignment: AlignmentType.CENTER,
              spacing: {
                after: 400,
              },
            }),
            // Claims
            ...claims.flatMap(claim => [
              // Claim heading
              new Paragraph({
                text: `Claim ${claim.number}`,
                heading: HeadingLevel.HEADING_2,
                spacing: {
                  before: 300,
                  after: 200,
                },
              }),
              // Claim text with bold numerals
              new Paragraph({
                children: parseClaimTextWithNumerals(claim.text),
                spacing: {
                  after: 300,
                  line: 360, // 1.5 line spacing
                },
              }),
            ]),
          ],
        }],
      });

      // Generate and download the document
      const blob = await Packer.toBlob(doc);
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'claims-with-numerals.docx';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      if (showToast) {
        showToast('Claims with numerals exported to Word document');
      }
    } catch (error) {
      console.error('Error exporting claims with numerals to DOCX:', error);
      if (showToast) {
        showToast('Error exporting claims with numerals');
      }
    }
  }

  const claims = isLegacyFormat 
    ? parseClaimsText(result.number_claims.output)
    : parseNewFormatClaims(result)

  const filteredClaims = claims.filter(claim => {
    if (!searchTerm) return true
    if (!matchesWildcard) return claim.text.toLowerCase().includes(searchTerm.toLowerCase())
    return matchesWildcard(claim.text, searchTerm)
  })

  const renderFormattedClaims = () => {
    if (searchTerm && filteredClaims.length === 0) {
      return (
        <div className={`text-center py-8 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
          <div className="text-lg mb-2">No claims found</div>
          <div className="text-sm">Try adjusting your search term or using wildcards (* for any characters)</div>
        </div>
      )
    }

    return (
      <div className="space-y-4">
        {filteredClaims.map((claim) => (
          <div
            key={claim.number}
            className={`p-4 rounded-lg border ${
              theme === 'dark' ? 'bg-gray-700 border-gray-600' : 'bg-white border-gray-200'
            }`}
          >
            <div className={`flex items-center justify-between mb-3`}>
              <div className={`flex items-center gap-3`}>
                <div className={`text-lg font-bold px-3 py-1 rounded-full ${
                  theme === 'dark' ? 'bg-blue-900 text-blue-300' : 'bg-blue-100 text-blue-800'
                }`}>
                  Claim {claim.number}
                </div>
                {claim.number === 1 && (
                  <span className={`text-xs px-2 py-1 rounded-full ${
                    theme === 'dark' ? 'bg-green-900 text-green-300' : 'bg-green-100 text-green-800'
                  }`}>
                    Independent
                  </span>
                )}
                {claim.number > 1 && (
                  <span className={`text-xs px-2 py-1 rounded-full ${
                    theme === 'dark' ? 'bg-purple-900 text-purple-300' : 'bg-purple-100 text-purple-800'
                  }`}>
                    Dependent
                  </span>
                )}
              </div>
            </div>
            
            <div
              className={`text-sm leading-relaxed ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}
              dangerouslySetInnerHTML={{
                __html: highlightNumerals(claim.text)
              }}
            />
          </div>
        ))}
        
        {/* Analysis metrics hidden but data objects preserved for later use */}
        {filteredClaims.length > 0 && false && (
          <div className={`mt-6 p-4 rounded-lg ${
            theme === 'dark' ? 'bg-gray-800 border border-gray-700' : 'bg-blue-50 border border-blue-200'
          }`}>
            <div className={`text-sm font-medium mb-2 ${theme === 'dark' ? 'text-gray-300' : 'text-blue-900'}`}>
              📊 Claims Summary
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
              <div className={`${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                <div className="font-medium">Total Claims</div>
                <div className={`text-lg font-bold ${theme === 'dark' ? 'text-blue-400' : 'text-blue-600'}`}>
                  {claims.length}
                </div>
              </div>
              <div className={`${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                <div className="font-medium">Independent</div>
                <div className={`text-lg font-bold ${theme === 'dark' ? 'text-green-400' : 'text-green-600'}`}>
                  {claims.filter(c => c.number === 1).length}
                </div>
              </div>
              <div className={`${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                <div className="font-medium">Dependent</div>
                <div className={`text-lg font-bold ${theme === 'dark' ? 'text-purple-400' : 'text-purple-600'}`}>
                  {claims.filter(c => c.number > 1).length}
                </div>
              </div>
              <div className={`${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                <div className="font-medium">Showing</div>
                <div className={`text-lg font-bold ${theme === 'dark' ? 'text-yellow-400' : 'text-yellow-600'}`}>
                  {filteredClaims.length}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    )
  }

  const renderStructuredData = () => {
    return (
      <pre className={`text-sm whitespace-pre-wrap font-mono p-4 rounded-md  overflow-y-auto ${
        theme === 'dark' ? 'bg-gray-700 text-gray-200' : 'bg-gray-100 text-gray-800'
      }`}>
        {JSON.stringify(result, null, 2)}
      </pre>
    )
  }

  return (
    <div className={showHeader ? "space-y-3" : ""}>
      {showHeader && (
        <div className="flex items-center justify-between">
          <div className={`text-lg font-medium ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
            📄 Claims with Reference Numerals
          </div>
          {showToggle && (
            <div className="flex items-center gap-2">
              {onAddToInput && (
                <button
                  onClick={() => {
                    onAddToInput(typeof result === 'object' ? JSON.stringify(result, null, 2) : result);
                    if (showToast) showToast('Result added to chat input!');
                  }}
                  className={`text-xs py-1 px-3 rounded-md transition-colors ${
                    theme === 'dark' ? 'bg-blue-600 text-white hover:bg-blue-500' : 'bg-blue-500 text-white hover:bg-blue-600'
                  }`}
                >
                  Add to Chat
                </button>
              )}
              <button
                onClick={exportToDocx}
                className={`text-xs py-1 px-3 rounded-md transition-colors flex items-center gap-1 ${
                  theme === 'dark' ? 'bg-green-600 text-white hover:bg-green-500' : 'bg-green-500 text-white hover:bg-green-600'
                }`}
                title="Export claims with numerals as Word document"
              >
                <Download className="w-3 h-3" />
                Export DOCX
              </button>
              <button
                onClick={() => {
                  if (onCopyResult) onCopyResult();
                }}
                className={`text-xs py-1 px-3 rounded-md transition-colors ${
                  theme === 'dark' ? 'bg-gray-600 text-gray-200 hover:bg-gray-500' : 'bg-gray-300 text-gray-700 hover:bg-gray-400'
                }`}
              >
                Copy
              </button>
              <button
                onClick={() => setShowStructured(!showStructured)}
                className={`text-xs py-1 px-3 rounded-md transition-colors ${
                  showStructured
                    ? theme === 'dark' ? 'bg-gray-600 text-gray-200 hover:bg-gray-500' : 'bg-gray-300 text-gray-700 hover:bg-gray-400'
                    : theme === 'dark' ? 'bg-blue-600 text-white hover:bg-blue-500' : 'bg-blue-500 text-white hover:bg-blue-600'
                }`}
              >
                {showStructured ? 'Show Formatted' : 'Show Raw Data'}
              </button>
            </div>
          )}
        </div>
      )}

      <div className="max-h-[calc(100vh-200px)] overflow-y-auto">
        {showStructured ? renderStructuredData() : renderFormattedClaims()}
      </div>
    </div>
  )
}

export default ClaimsWithNumeralsDisplay